// Shell surface slot resolver (DECISION-053 Slice-2) — PURE (no THREE / no DOM). Maps a shell
// GLB node name → a canonical surface SLOT id (wall/floor/ceiling/counter/fixture), which the
// composer then binds to a material via templateSurfaceMaterials[templateId][slot] (data).
//
// Retires the old SHELL_PATTERNS if/else regex chain, whose correctness depended on STATEMENT
// ORDER (counter tested before fixture, guarded by a fragile negative-lookahead). Here each slot
// is a data rule with an explicit SPECIFICITY; the highest-specificity match wins regardless of
// order — so `Shell_LongboxCounter` resolves to `counter` (specificity 3) not `fixture`
// (specificity 1), no lookahead needed. The shell authoring convention is `Shell_<Slot>[_n]`;
// when Content bakes real per-node slot ids this table becomes the single place to read them.

// Ordered by specificity DESC only for readability — resolution picks the max, not the first.
const SLOT_RULES = [
	{ slot: "counter", specificity: 3, test: /^Shell_LongboxCounter/i },
	{ slot: "wall",    specificity: 2, test: /^Shell_Wall(_|$)/i },
	// E2 (ASK-100 ④): floor/ceiling were EXACT-anchored (/^Shell_Floor$/) — correct for a single
	// authored shell, wrong for a COOKED multi-module shell: the D-057 cook preserves authored
	// names verbatim (S2), so three modules each ship `Shell_Floor`, and THREE's GLTFLoader
	// uniquifies duplicates at load (`createUniqueName` → Shell_Floor / Shell_Floor_1 / _2).
	// Exact rules bound only the FIRST — corridor + room floors/ceilings kept the authored
	// placeholder (Shaun's "plain white"). Walls never hit this because their rule was already
	// `(_|$)`-boundary-tolerant; floor/ceiling now match the same convention (`Shell_<Slot>[_n]`).
	{ slot: "floor",   specificity: 2, test: /^Shell_Floor(_|$)/i },
	{ slot: "ceiling", specificity: 2, test: /^Shell_Ceiling(_|$)/i },
	{ slot: "door",    specificity: 2, test: /^Shell_Door(_|$)/i },
	// Retired: the `fixture` rule (/^Shell_(Longbox|Shortbox|Table|BookShelf|WallShelf)/) matched
	// shell-BAKED fixture furniture that no longer exists — fixtures render via the composer
	// (DECISION-052), so those meshes were removed from the shell (coord 2026-07-02).
];

/** Canonical surface slot for a shell node name, or null if it isn't a skinnable shell surface.
 * Highest-specificity matching rule wins (order-independent). */
export function resolveShellSlot(nodeName) {
	const name = String(nodeName || "");
	let best = null;
	for (const r of SLOT_RULES) {
		if (r.test.test(name) && (!best || r.specificity > best.specificity)) best = r;
	}
	return best ? best.slot : null;
}

/** The canonical slot ids, in compose order (stable scene-load layering). */
export const SHELL_SLOTS = Object.freeze(["wall", "floor", "ceiling", "counter", "door"]);

export { SLOT_RULES };
